'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { StoreProduct } from '@medusajs/types'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { searchProducts } from '@onclicks/sharedmedusa/medusaData/products'

import { useDebounce } from '@/utilities/useDebounce'
import SearchInput from './SearchInput'

export default function SearchButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<StoreProduct[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedQuery = useDebounce(query, 300)

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([])
      return
    }

    let isCancelled = false
    setIsLoading(true)

    searchProducts(debouncedQuery)
      .then((products) => {
        if (!isCancelled) {
          setResults(products)
        }
      })
      .catch((error) => {
        console.error('Erreur lors de la recherche de produits:', error)
        if (!isCancelled) {
          setResults([])
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [debouncedQuery])

  const handleClose = () => {
    setIsOpen(false)
    setQuery('')
    setResults([])
  }

  // Ferme le panneau quand le focus quitte le conteneur (bouton + input + résultats)
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(event.relatedTarget as Node)) {
      handleClose()
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose()
        }
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center justify-center w-10 h-10"
        aria-label="Rechercher un produit"
      >
        <MagnifyingGlassIcon width={24} height={24} className="text-primary" />
      </button>

      {isOpen && (
        <SearchInput
          inputRef={inputRef}
          query={query}
          setQuery={setQuery}
          isLoading={isLoading}
          results={results}
          handleClose={handleClose}
        />
      )}
    </div>
  )
}
